import { useState } from 'react'
import { useAppSelector } from '@/store/store-hooks'
import { ChatListSidebarItem } from '../../conversation_list/schema'
import { useNavigate, useParams } from 'react-router-dom'
import { RootState } from '@/store/store'
import { buildChatFeaturePath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { ModalFooterWithActionsType } from '@/components/modal/constants'

type DeleteChatModalProps = {
  dialogVisible: boolean
  chatItem: ChatListSidebarItem | null
  onDialogClose: () => void
  deleteConversation: (conversationId: string) => Promise<void>
  /** Embedded workspaces can keep their parent page open after deletion. */
  onDeleted?: () => void
}

export default function DeleteChatModal(props: DeleteChatModalProps) {
  const { dialogVisible, chatItem, onDialogClose, deleteConversation, onDeleted } = props
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<boolean>(false)

  const navigate = useNavigate()

  const { chatId } = useParams()
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)

  const handleConfirm = async () => {
    setLoading(true)
    setError(false)

    if (!chatItem) {
      console.error('No conversation to delete.')
      return
    }

    try {
      await deleteConversation(chatItem.id)

      // Embedded callers own navigation; standalone chats return to the chat start page.
      if (onDeleted) {
        onDeleted()
      } else if (chatId === chatItem.id && activeMatterId) {
        navigate(buildChatFeaturePath(activeMatterId), { replace: true })
      }

      // Short delay before closing the dialog
      await new Promise((resolve) => setTimeout(resolve, 500))

      onDialogClose()
    } catch (err) {
      console.error('Error while deleting conversation:', err)
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setError(false)
      onDialogClose()
    }
  }

  return (
    <Modal open={dialogVisible} onOpenChange={handleOpenChange}>
      <ModalContent
        title="Delete conversation?"
        subtitle={`You are about to permanently delete "${chatItem?.title}". Please confirm.`}
        width="md"
        height="max-md"
        footer={
          <ModalFooterWithActions
            type={ModalFooterWithActionsType.DESTRUCTIVE}
            secondaryAction={{ label: 'Cancel', onClick: () => handleOpenChange(false), disabled: loading }}
            primaryAction={{ label: 'Delete', onClick: handleConfirm, loading }}
            inlineMessage={error ? { variant: 'error', children: 'Failed to delete chat. Please try again.' } : undefined}
          />
        }
      />
    </Modal>
  )
}
